import { base64decode } from "@protobuf-ts/runtime"
import { TwirpFetchTransport } from "@protobuf-ts/twirp-transport"
import { SerializedError } from "@reduxjs/toolkit"
import { BaseQueryFn, createApi } from "@reduxjs/toolkit/query/react"
import { Any as AnyPb } from "gen/google/protobuf/any_pb"
import {
  type ApiError,
  getUserMessage,
  isApiError,
} from "shared-ts/api/apiError"
import { nonNullish } from "shared-ts/utils/nullish/nullish"

import { stringify } from "lib/stringify"

import type { UnaryCall } from "@protobuf-ts/runtime-rpc"

export { type ApiError, getUserMessage, isApiError }

export type RtkError = ApiError | SerializedError

/**
 * Whether the request was cancelled by `promise.abort()` rather than failing.
 * RTK Query reports both through the same `{ error }` channel, so an effect that
 * aborts its in-flight request on cleanup has to filter these out before showing
 * the reader anything -- otherwise every re-run reports a failure that isn't one.
 */
export function isAbortError(error: RtkError): boolean {
  return error.name === "AbortError"
}

const baseUrl =
  process.env.NEXT_PUBLIC_VERCEL_ENV === "development"
    ? "/api/twirp"
    : `${process.env.NEXT_PUBLIC_RUNDOO_API_BASE_URL}/twirp`

/** Twirp transport configuration for API calls */
export const transport = new TwirpFetchTransport({
  baseUrl,
  fetchInit: { credentials: "include" },
  sendJson: true,
  jsonOptions: { ignoreUnknownFields: true },
})

/** Base query for Twirp RPC calls through RTK Query. */
const baseQuery: BaseQueryFn<UnaryCall, unknown, ApiError> = async (
  rpcCall
) => {
  try {
    const result = await rpcCall
    return { data: result.response }
  } catch (error: unknown) {
    // Catch protobuf RpcError and cast to ApiError
    // (SerializedError only occurs in RTK Query's internal handling)
    return { error: extractProtoAny(error as ApiError) }
  }
}

/**
 * This apiSlice should remain empty.
 * Add new endpoints with enhanceEndpoints and injectEndpoints.
 */
export const apiSlice = createApi({
  reducerPath: "api",
  baseQuery,
  endpoints: () => ({}),
  serializeQueryArgs: ({ queryArgs, endpointName }) =>
    `${endpointName}(${stringify(queryArgs)})`,
})

/** Decodes protobuf Any stuffed in meta.protoAnyError */
export const extractProtoAny = (error: ApiError): ApiError => {
  if (!nonNullish(error.meta.protoAnyError)) return error

  const b64 = base64decode(String(error.meta.protoAnyError))
  error.protoError = AnyPb.fromBinary(b64)

  return error
}
